import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that displays a cart gift note (Twig and Bloom custom).
 */
class CartGiftNote extends Component {
  #activeFetch = null;
  #maxLength = 200;

  connectedCallback() {
    super.connectedCallback?.();

    this.textarea = this.querySelector('#cart-gift-note');
    this.remaining = this.querySelector('#rchars');
    this.remainingMsg = this.querySelector('#rcharmsg');
    this.error = this.querySelector('#notemsgerror');
    this.checkoutButton = document.querySelector('#checkout');

    this.textarea?.addEventListener('input', this.updateCounter);
    this.checkoutButton?.addEventListener('click', this.validateBeforeCheckout, true);

    this.updateCounter();
  }

  disconnectedCallback() {
    this.textarea?.removeEventListener('input', this.updateCounter);
    this.checkoutButton?.removeEventListener('click', this.validateBeforeCheckout, true);
    super.disconnectedCallback?.();
  }

  updateCounter = () => {
    if (!this.textarea) return;

    const remaining = this.#maxLength - this.textarea.value.length;

    if (this.remaining) this.remaining.textContent = remaining;
    if (this.remainingMsg) {
      this.remainingMsg.textContent =
        remaining === 1 ? ' Character Remaining' : ' Characters Remaining';
    }

    if (this.error) this.error.textContent = '';

    this.emitValidityChange();
  };

  validateBeforeCheckout = (event) => {
    if (!this.textarea) return;

    if (this.textarea.value.trim() === '') {
      event.preventDefault();
      event.stopImmediatePropagation();

      if (this.error) {
        this.error.textContent =
          'Please enter a message or type N/A if no message applicable';
      }

      this.textarea.focus();
      return false;
    }
  };

  emitValidityChange = () => {
    if (!this.textarea) return;

    const value = this.textarea.value;
    const valid = value.trim() !== '';

    this.dispatchEvent(
      new CustomEvent('cart-gift-note:change', {
        bubbles: true,
        detail: {
          value,
          valid,
        },
      })
    );
  };

  updateCartGiftNote = debounce(async (event) => {
    if (!(event.target instanceof HTMLTextAreaElement)) return;

    this.updateCounter();

    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const giftNote = event.target.value;

      const config = fetchConfig('json', {
        body: JSON.stringify({
          attributes: {
            CartGiftNote: giftNote,
          },
        }),
      });

      await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(error);
      }
    } finally {
      if (this.#activeFetch === abortController) {
        this.#activeFetch = null;
      }

      cartPerformance.measureFromEvent('gift-note-update:user-action', event);
    }
  }, 200);
}

if (!customElements.get('cart-gift-note')) {
  customElements.define('cart-gift-note', CartGiftNote);
}